import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled, {css} from 'styled-components';
import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {useFormikContext} from 'formik';
import {formatPrice} from 'shared/helpers';
import Icon from 'shared/Icon';
import {
    CostingMethod,
    GroupSelectionMode,
    PriceType,
} from 'shared/constants/PackagingOption';
import CollapseContainer from './components/CollapseContainer';
import {useGetJobCostQuery} from './store/jobApi';
import {
    CustomerDeliveryExtraGroups,
    DeliveryExtraGroup,
    PackagingOption,
    useGetCustomerDeliveryExtraGroupsQuery,
    useGetJobPackagingOptionsQuery,
    useReconcileJobPackagingOptionsMutation,
} from './store/packagingOptionApi';

const EMPTY_DELIVERY_EXTRA_GROUPS: DeliveryExtraGroup[] = [];

interface JobFormValues {
    depotId?: number | string;
    dispatch: number | string;
    street?: string;
    address?: string;
    suburb?: string;
    city?: string;
    state?: number | string;
    postcode?: number | string;
    packagingOptionIds: number[];
    hasPackagingOptions: boolean;
    packagingOptionsLoaded: boolean;
    packagingOptionsValid: boolean;
}

interface Props {
    jobId?: number;
    isEditing: boolean;
    enforceRequiredGroups?: boolean;
}

const PackagingOptionsCollapseContainer = styled(CollapseContainer)`
    margin-bottom: 15px;
`;

const GroupOptions = styled.fieldset`
    min-width: 0;
    margin: 0 0 10px;
    padding: 0;
    border: 0;

    /* Blue subheading pill, same pattern as the Saved Addresses heading. */
    legend {
        width: fit-content;
        margin: 7px 0 0 4px;
        padding: 6px 12px;
        border-top-left-radius: 15px;
        border-top-right-radius: 15px;
        background: rgb(var(--primary_colour));
        color: #fff;
        font-size: 0.875rem;
        font-weight: 500;
    }
`;

const OptionList = styled.div`
    display: flex;
    flex-direction: column;
    border-top: 1px solid #e0e0e0;
`;

/* Flat list row, same pattern as a Saved Addresses row: bottom divider, selected row
    tinted with the table's primary row colour, unselected rows tint on hover. */
const OptionRow = styled.div<{$selected: boolean}>`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    padding: 10px 14px;
    border-bottom: 1px solid #e0e0e0;
    background: ${({$selected}) =>
        $selected ? 'rgb(var(--table_row_primary))' : 'transparent'};
    color: #364353;
    font-size: 0.875rem;
    cursor: default;

    &:hover {
        background: ${({$selected}) =>
            $selected ? 'rgb(var(--table_row_primary))' : '#f5f5f5'};
    }

    &:focus-within {
        box-shadow: inset 0 0 0 2px rgb(var(--primary_colour));
    }
`;

const OptionSelectLabel = styled.label`
    display: flex;
    flex: 1;
    align-items: center;
    gap: 10px;
    min-width: 0;
    margin: 0;
    cursor: pointer;
`;

/* Visual radio, same pattern as the address CustomRadio: blue-ringed circle when
    unchecked, tick icon when checked. The real input stays on top, visually hidden. */
const OptionRadio = styled.span<{$selected: boolean}>`
    position: relative;
    flex: 0 0 17px;
    width: 17px;
    height: 17px;

    ${({$selected}) =>
        !$selected &&
        css`
            border: 2px solid rgb(var(--primary_colour));
            border-radius: 17px;
            background: #fff;
        `}

    img {
        display: block;
        width: 17px;
        height: 17px;
    }
`;

const OptionInput = styled.input`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    cursor: pointer;
`;

const OptionInfo = styled.span`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const OptionTop = styled.span`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
`;

const OptionName = styled.strong`
    color: #26384a;
    font-size: 0.875rem;
    overflow-wrap: anywhere;
`;

const OptionDescription = styled.span`
    color: #5d6978;
    font-size: 0.8125rem;
    line-height: 1.4;
    overflow-wrap: anywhere;
`;

const Price = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    flex-shrink: 0;
    color: #26384a;
    font-size: 0.875rem;
    font-weight: 700;
    text-align: right;
`;

const PriceBasis = styled.small`
    color: #4b5968;
    font-size: 0.6875rem;
    font-weight: 500;
    white-space: nowrap;
`;

const PriceRate = styled.span`
    font-weight: 700;
    white-space: nowrap;
`;

const ExtraLineLabel = styled.span`
    display: inline-flex;
    align-items: center;
    min-width: 0;
    gap: 6px;
`;

const PriceInfoButton = styled.button`
    display: inline-flex;
    flex: 0 0 26px;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 4px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    cursor: help;

    &:hover,
    &:focus-visible {
        background: #eaf1f8;
        outline: 2px solid rgb(var(--primary_colour));
        outline-offset: 1px;
    }

    img {
        display: block;
        width: 16px;
        height: 16px;
    }
`;

const PriceInfoContainer = styled.span`
    position: relative;
    display: inline-flex;
    flex: 0 0 26px;
    align-items: center;
    justify-content: center;

    &:hover > [role='tooltip'],
    &:focus-within > [role='tooltip'] {
        visibility: visible;
        opacity: 1;
    }
`;

const PriceInfoTooltip = styled.div<{$openRight?: boolean}>`
    position: absolute;
    right: 0;
    bottom: calc(100% + 8px);
    z-index: 1100;
    width: max-content;
    max-width: min(320px, calc(100vw - 24px));
    padding: 8px 10px;
    border-radius: 4px;
    background: #26384a;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    color: #fff;
    font-size: 0.8125rem;
    text-align: left;
    visibility: hidden;
    opacity: 0;
    pointer-events: auto;
    transition: opacity 0.12s ease;

    ${({$openRight}) =>
        $openRight &&
        css`
            right: auto;
            left: 0;
        `}
`;

const PriceTooltipContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-weight: 400;
    line-height: 1.4;
`;

interface PricingDetailsProps {
    tooltipId: string;
    label: string;
    details: string[];
    openRight?: boolean;
}

const renderPricingDetails = ({
    tooltipId,
    label,
    details,
    openRight,
}: PricingDetailsProps): React.ReactNode =>
    details.length ? (
        <PriceInfoContainer>
            <PriceInfoButton
                type="button"
                aria-label={label}
                aria-describedby={tooltipId}>
                <span aria-hidden="true">
                    <Icon iconName="More-Info.svg" showAltText={false} />
                </span>
            </PriceInfoButton>
            <PriceInfoTooltip
                id={tooltipId}
                role="tooltip"
                $openRight={openRight}>
                <PriceTooltipContent>
                    {details.map((detail) => (
                        <div key={`${tooltipId}-${detail}`}>{detail}</div>
                    ))}
                </PriceTooltipContent>
            </PriceInfoTooltip>
        </PriceInfoContainer>
    ) : null;

const Estimate = styled.span`
    padding: 2px 7px;
    border-radius: 12px;
    background: #fff1d6;
    color: #8a4b08;
    font-size: 0.6875rem;
    font-weight: 600;
`;

const Message = styled.div<{$warning?: boolean}>`
    margin: 10px 14px;
    padding: 10px 12px;
    border-left: 3px solid ${({$warning}) => ($warning ? '#ba7414' : '#24477f')};
    border-radius: 4px;
    background: ${({$warning}) => ($warning ? '#fff7e7' : '#f1f6fb')};
    color: #405165;
    font-size: 0.875rem;
    line-height: 1.45;
`;

const ExtraBreakdown = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 14px;
    padding: 12px 14px 0;
    border-top: 1px solid #dce2e9;
`;

const ExtraLine = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    color: #364353;
    font-size: 0.875rem;
    line-height: 1.4;
`;

const ExtraTotal = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 14px;
    padding: 11px 14px;
    border-top: 1px solid #dce2e9;
    color: #26384a;
    font-weight: 700;
`;

const getOptionPrice = (
    option: PackagingOption,
    productCount: number | null,
    jobSubtotal: number | null | undefined
): number | null => {
    const costingMethod = Number(option.costing_method);
    if (costingMethod === CostingMethod.TO_BE_QUOTED) return null;
    if (costingMethod === CostingMethod.BY_DISTANCE) {
        return option.calculated_amount == null
            ? null
            : Number(option.calculated_amount);
    }
    const configuredAmount = option.customer_amount ?? option.amount;
    if (configuredAmount == null || String(configuredAmount) === '')
        return null;
    const amount = Number(configuredAmount);
    if (!Number.isFinite(amount)) return null;
    if (costingMethod === CostingMethod.PERCENTAGE) {
        if (jobSubtotal == null || !Number.isFinite(Number(jobSubtotal))) {
            return null;
        }
        return (Number(jobSubtotal) * amount) / 100;
    }
    if (costingMethod === CostingMethod.FIXED_PRODUCT) {
        return productCount !== null && Number.isFinite(productCount)
            ? productCount * amount
            : null;
    }
    return amount;
};

const getPriceLabel = (
    option: PackagingOption,
    price: number | null,
    currencyType: string
): React.ReactNode => {
    const costingMethod = Number(option.costing_method);
    if (option.distance_pending) return 'Waiting for delivery distance';
    if (
        costingMethod === CostingMethod.TO_BE_QUOTED ||
        option.price_confirmed_after_review
    ) {
        return 'Price confirmed after review';
    }
    if (price === null) {
        const configuredAmount = option.customer_amount ?? option.amount;
        if (
            configuredAmount == null ||
            String(configuredAmount) === '' ||
            !Number.isFinite(Number(configuredAmount))
        ) {
            return 'Price confirmed after review';
        }
        const amount = Number(configuredAmount);
        if (costingMethod === CostingMethod.PERCENTAGE) {
            return `${amount}% of job subtotal`;
        }
        if (costingMethod === CostingMethod.FIXED_PRODUCT) {
            return `${String(formatPrice(amount, {currencyType}))} per product`;
        }
        return String(formatPrice(amount, {currencyType}));
    }
    const formatted = String(formatPrice(price, {currencyType}));
    return Number(option.price_type) === PriceType.INDICATIVE ? (
        <>
            <Estimate title="Your manufacturer will confirm this price before you pay.">
                Estimate
            </Estimate>
            {formatted}
        </>
    ) : (
        formatted
    );
};

const formatPriceText = (amount: number, currencyType: string): string =>
    String(formatPrice(amount, {currencyType}));

const formatPercentage = (amount: number): string =>
    Number(amount).toLocaleString(undefined, {maximumFractionDigits: 2});

const getOptionListPriceLabel = (
    option: PackagingOption,
    price: number | null,
    currencyType: string
): React.ReactNode => {
    const costingMethod = Number(option.costing_method);
    const configuredAmount = option.customer_amount ?? option.amount;
    if (
        (costingMethod === CostingMethod.FIXED_PRODUCT ||
            costingMethod === CostingMethod.PERCENTAGE) &&
        configuredAmount != null &&
        String(configuredAmount) !== '' &&
        Number.isFinite(Number(configuredAmount))
    ) {
        const amount = Number(configuredAmount);
        return (
            <>
                {Number(option.price_type) === PriceType.INDICATIVE ? (
                    <Estimate title="Your manufacturer will confirm this price before you pay.">
                        Estimate
                    </Estimate>
                ) : null}
                <PriceRate>
                    {costingMethod === CostingMethod.FIXED_PRODUCT
                        ? String(formatPrice(amount, {currencyType}))
                        : `${formatPercentage(amount)}%`}
                </PriceRate>
                <PriceBasis>
                    {costingMethod === CostingMethod.FIXED_PRODUCT
                        ? 'per product'
                        : 'of job subtotal'}
                </PriceBasis>
            </>
        );
    }

    return getPriceLabel(option, price, currencyType);
};

const getOptionPricingDetails = (
    option: PackagingOption,
    price: number | null,
    productCount: number | null,
    jobSubtotal: number | null | undefined,
    isEditing: boolean,
    currencyType: string
): string[] => {
    const costingMethod = Number(option.costing_method);
    if (costingMethod === CostingMethod.TO_BE_QUOTED) {
        return [
            'No price is calculated for this option before submission.',
            'Your manufacturer will confirm the price after reviewing the job.',
        ];
    }

    const configuredAmount = option.customer_amount ?? option.amount;
    if (
        costingMethod !== CostingMethod.BY_DISTANCE &&
        (configuredAmount == null ||
            String(configuredAmount) === '' ||
            !Number.isFinite(Number(configuredAmount)))
    ) {
        return [
            'The price will be confirmed by your manufacturer after review.',
        ];
    }

    const amount = Number(configuredAmount ?? 0);
    const customerRateValue =
        costingMethod === CostingMethod.PERCENTAGE
            ? `${formatPercentage(amount)}% of job subtotal`
            : costingMethod === CostingMethod.FIXED_PRODUCT
              ? `${formatPriceText(amount, currencyType)} per product`
              : `${formatPriceText(amount, currencyType)} per job`;
    const customerRateNote =
        option.customer_amount == null
            ? null
            : `Customer-specific rate applies: ${customerRateValue}.`;
    const gstNote = 'Any applicable GST is added to the job total.';
    const withEstimateNote = (details: string[]): string[] =>
        Number(option.price_type) === PriceType.INDICATIVE
            ? [
                  ...details,
                  'This is an estimate; your manufacturer confirms it before payment.',
              ]
            : details;

    if (costingMethod === CostingMethod.FIXED_JOB) {
        return withEstimateNote([
            customerRateNote ||
                `Fixed job price: ${formatPriceText(price ?? amount, currencyType)}.`,
            'This amount is charged once for the whole job.',
            gstNote,
        ]);
    }

    if (costingMethod === CostingMethod.FIXED_PRODUCT) {
        const unitPrice = formatPriceText(amount, currencyType);
        if (productCount == null || !isEditing) {
            return withEstimateNote([
                customerRateNote || `Rate: ${unitPrice} per product.`,
                isEditing
                    ? 'The job product count is not available yet. The total uses all quantities in Products, Benchtops and Sundries.'
                    : 'This new job has no product quantities yet. The total is the rate multiplied by quantities in Products, Benchtops and Sundries.',
                gstNote,
            ]);
        }

        const total = price ?? productCount * amount;
        return withEstimateNote([
            `${productCount} ${productCount === 1 ? 'product' : 'products'} × ${unitPrice} per product = ${formatPriceText(total, currencyType)}.`,
            'Product count includes quantities in Products, Benchtops and Sundries.',
            ...(customerRateNote ? [customerRateNote] : []),
            gstNote,
        ]);
    }

    if (costingMethod === CostingMethod.PERCENTAGE) {
        const rate = `${formatPercentage(amount)}%`;
        const subtotalNote =
            'Job subtotal includes line items and excludes minimum usage fees, default freight and other Delivery & Extras options.';
        if (jobSubtotal == null || !Number.isFinite(Number(jobSubtotal))) {
            return withEstimateNote([
                customerRateNote || `Rate: ${rate} of the job subtotal.`,
                isEditing
                    ? 'The job subtotal is not available yet. The amount updates when it loads.'
                    : 'This new job has no product subtotal yet. The amount updates when its products are added.',
                subtotalNote,
                gstNote,
            ]);
        }

        const calculatedTotal = price ?? (Number(jobSubtotal) * amount) / 100;
        return withEstimateNote([
            `${rate} × ${formatPriceText(Number(jobSubtotal), currencyType)} job subtotal = ${formatPriceText(calculatedTotal, currencyType)}.`,
            subtotalNote,
            ...(customerRateNote ? [customerRateNote] : []),
            gstNote,
        ]);
    }

    if (costingMethod === CostingMethod.BY_DISTANCE) {
        if (option.distance_pending) {
            return [
                'Distance is the driving distance from the dispatching depot (or manufacturer address if depots are off) to the delivery address.',
                'The matching distance band and price will appear when the distance is calculated.',
            ];
        }
        if (option.price_confirmed_after_review || option.distance_km == null) {
            return [
                'A distance band could not be priced for this delivery.',
                'Your manufacturer will confirm the price after reviewing the job.',
            ];
        }

        const distance = Number(option.distance_km);
        const band = option.distance_bands.find(
            (item) =>
                distance >= Number(item.from_km) &&
                (item.to_km == null || distance < Number(item.to_km))
        );
        if (!band) {
            return [
                'No configured distance band covers this delivery.',
                'Your manufacturer will confirm the price after reviewing the job.',
            ];
        }

        const range =
            band.to_km == null
                ? `${formatPercentage(Number(band.from_km))} km and over`
                : `${formatPercentage(Number(band.from_km))}–${formatPercentage(Number(band.to_km))} km band`;
        const details = [
            `${formatPercentage(distance)} km falls in the ${range}.`,
            `Band price: ${formatPriceText(Number(band.price), currencyType)}.`,
        ];
        if (option.customer_amount != null) {
            const adjustment = Number(option.customer_amount);
            const signedAdjustment =
                adjustment > 0
                    ? `+${formatPercentage(adjustment)}`
                    : formatPercentage(adjustment);
            details.push(
                `Customer rate adjustment: ${signedAdjustment}% on the band price.`
            );
        }
        const calculatedPrice = price ?? option.calculated_amount;
        if (calculatedPrice == null) {
            return [
                'A distance band could not be priced for this delivery.',
                'Your manufacturer will confirm the price after reviewing the job.',
            ];
        }
        const priceExplanation =
            option.customer_amount == null
                ? `Price for this delivery: ${formatPriceText(calculatedPrice, currencyType)}.`
                : `Price = ${formatPriceText(Number(band.price), currencyType)} × (1 ${Number(option.customer_amount) >= 0 ? '+' : '−'} ${formatPercentage(Math.abs(Number(option.customer_amount)))}%) = ${formatPriceText(calculatedPrice, currencyType)}.`;
        details.push(priceExplanation, gstNote);
        return withEstimateNote(details);
    }

    return [
        'The displayed amount is calculated from this option’s pricing method.',
    ];
};

const PackagingOptionsSection = ({
    jobId,
    isEditing,
    enforceRequiredGroups = false,
}: Props) => {
    const {userProfile} = useAppContext();
    const {job} = useJobContext() as PartialJob;
    const productCount =
        !isEditing ||
        job?.totalProductCount == null ||
        !Number.isFinite(Number(job.totalProductCount))
            ? null
            : Number(job.totalProductCount);
    const {values, setFieldValue} = useFormikContext<JobFormValues>();
    const depotId = Number(values.depotId) || undefined;
    const dispatchMethod = Number(values.dispatch) || 0;
    const deliveryAddress =
        dispatchMethod === 0
            ? ''
            : [
                  values.street || values.address,
                  values.suburb,
                  values.city,
                  values.state,
                  values.postcode,
              ]
                  .map((part) => String(part || '').trim())
                  .filter(Boolean)
                  .join(', ');
    const distanceContextKey = `${dispatchMethod}|${depotId || ''}|${deliveryAddress}`;
    const [resolvedDistanceContext, setResolvedDistanceContext] = useState('');
    const resolveDistance = resolvedDistanceContext === distanceContextKey;
    const {
        currentData: currentAvailableData,
        isLoading: isLoadingAvailable,
        isFetching: isFetchingAvailable,
        isError: availableGroupsError,
        refetch: refetchGroups,
    } = useGetCustomerDeliveryExtraGroupsQuery(
        {depotId, dispatchMethod, deliveryAddress, resolveDistance},
        {refetchOnMountOrArgChange: true}
    );
    const [distanceLookupData, setDistanceLookupData] = useState<{
        contextKey: string;
        data: CustomerDeliveryExtraGroups;
    } | null>(null);
    useEffect(() => {
        if (currentAvailableData && !resolveDistance) {
            setDistanceLookupData({
                contextKey: distanceContextKey,
                data: currentAvailableData,
            });
        }
    }, [currentAvailableData, distanceContextKey, resolveDistance]);
    const availableData: CustomerDeliveryExtraGroups | undefined =
        currentAvailableData ||
        (resolveDistance &&
        distanceLookupData?.contextKey === distanceContextKey
            ? distanceLookupData.data
            : undefined);
    const groups = availableData?.groups ?? EMPTY_DELIVERY_EXTRA_GROUPS;
    const {
        currentData: savedOptionsResult,
        isLoading: isLoadingSaved,
        isFetching: isFetchingSaved,
        isSuccess: savedOptionsLoaded,
        isError: savedOptionsError,
        refetch: refetchSavedOptions,
    } = useGetJobPackagingOptionsQuery(
        {jobId: jobId || 0},
        {skip: !jobId, refetchOnMountOrArgChange: true}
    );
    const savedOptions = savedOptionsResult?.options ?? [];
    const savedOptionIds = useMemo(
        () =>
            new Set(
                savedOptions
                    .map((option) => option.packaging_option_id)
                    .filter(
                        (optionId): optionId is number =>
                            optionId !== null &&
                            Number.isSafeInteger(Number(optionId))
                    )
                    .map(Number)
            ),
        [savedOptions]
    );
    const isLegacyEditJob =
        isEditing &&
        savedOptionsLoaded &&
        !savedOptionsError &&
        savedOptionsResult !== undefined &&
        savedOptions.length === 0;
    const {currentData: jobCost, isFetching: isFetchingJobCost} =
        useGetJobCostQuery(
            {jobId: jobId || 0},
            {skip: !jobId, refetchOnMountOrArgChange: true}
        );
    const jobSubtotalForOptions = isFetchingJobCost
        ? undefined
        : jobCost?.jobSubtotal;
    const [reconcileJobOptions, {isLoading: isReconciling}] =
        useReconcileJobPackagingOptionsMutation();
    const [reconciledJobId, setReconciledJobId] = useState<number | null>(
        isEditing && jobId ? null : Number(jobId) || null
    );
    const [reconcileFailed, setReconcileFailed] = useState(false);
    const [reconcileAttempt, setReconcileAttempt] = useState(0);
    const isReconciled =
        !isEditing || (!!jobId && reconciledJobId === Number(jobId));
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectionAdjusted, setSelectionAdjusted] = useState(false);
    const selectedRef = useRef<number[]>([]);
    const manuallyDeselectedRef = useRef<Set<number>>(new Set());
    const previousAvailableRef = useRef<Set<number>>(new Set());
    const previousAvailabilityContextRef = useRef('');
    const initializedRef = useRef(false);
    const initializedJobIdRef = useRef<number | null>(null);
    const visibleOptionIds = useMemo(
        () =>
            new Set(
                groups.flatMap((group) =>
                    group.options.map((option) => option.id)
                )
            ),
        [groups]
    );
    const shouldEnforceRequiredGroups =
        enforceRequiredGroups || !isLegacyEditJob || selectedIds.length > 0;
    const missingRequiredGroups = useMemo(
        () =>
            shouldEnforceRequiredGroups
                ? groups.filter(
                      (group) =>
                          group.selection_mode ===
                              GroupSelectionMode.REQUIRED &&
                          !group.options.some((option) =>
                              selectedIds.includes(option.id)
                          )
                  )
                : [],
        [groups, selectedIds, shouldEnforceRequiredGroups]
    );
    const hasIndicativeSelection = useMemo(
        () =>
            groups.some((group) =>
                group.options.some(
                    (option) =>
                        selectedIds.includes(option.id) &&
                        (Number(option.price_type) === PriceType.INDICATIVE ||
                            Number(option.costing_method) ===
                                CostingMethod.TO_BE_QUOTED ||
                            option.price_confirmed_after_review ||
                            option.distance_pending ||
                            getOptionPrice(
                                option,
                                productCount,
                                jobSubtotalForOptions
                            ) === null)
                )
            ),
        [groups, productCount, jobSubtotalForOptions, selectedIds]
    );
    const selectedExtraTotal = useMemo(() => {
        let total = 0;
        groups.forEach((group) =>
            group.options.forEach((option) => {
                if (!selectedIds.includes(option.id)) return;
                const price = getOptionPrice(
                    option,
                    productCount,
                    jobSubtotalForOptions
                );
                if (price !== null) {
                    total += Math.round(price * 100) / 100;
                }
            })
        );
        return Math.round(total * 100) / 100;
    }, [groups, productCount, jobSubtotalForOptions, selectedIds]);
    const hasPendingOptionCalculation = useMemo(
        () =>
            groups.some((group) =>
                group.options.some((option) => {
                    if (!selectedIds.includes(option.id)) return false;
                    const costingMethod = Number(option.costing_method);
                    if (costingMethod === CostingMethod.FIXED_PRODUCT) {
                        return productCount == null;
                    }
                    if (costingMethod === CostingMethod.PERCENTAGE) {
                        return jobSubtotalForOptions == null;
                    }
                    return (
                        costingMethod === CostingMethod.BY_DISTANCE &&
                        option.distance_pending
                    );
                })
            ),
        [groups, jobSubtotalForOptions, productCount, selectedIds]
    );
    const preliminaryCostUnavailableMessage = useMemo(() => {
        const selectedOptions = groups.flatMap((group) =>
            group.options.filter((option) => selectedIds.includes(option.id))
        );
        if (
            selectedOptions.some(
                (option) =>
                    Number(option.costing_method) ===
                        CostingMethod.FIXED_PRODUCT && productCount == null
            )
        ) {
            return isEditing
                ? 'Job quantities are not available yet.'
                : 'Add products to see the preliminary cost.';
        }
        if (
            selectedOptions.some(
                (option) =>
                    Number(option.costing_method) ===
                        CostingMethod.PERCENTAGE &&
                    jobSubtotalForOptions == null
            )
        ) {
            return isEditing
                ? 'Job subtotal is not available yet.'
                : 'Add products to see the preliminary cost.';
        }
        if (
            selectedOptions.some(
                (option) =>
                    Number(option.costing_method) ===
                        CostingMethod.BY_DISTANCE && option.distance_pending
            )
        ) {
            return 'Delivery distance is not available yet.';
        }
        return 'Job pricing details are not available yet.';
    }, [groups, isEditing, jobSubtotalForOptions, productCount, selectedIds]);
    const selectedExtraLines = useMemo(() => {
        return groups.flatMap((group) =>
            group.options.flatMap((option) => {
                if (!selectedIds.includes(option.id)) return [];
                const price = getOptionPrice(
                    option,
                    productCount,
                    jobSubtotalForOptions
                );
                const costingMethod = Number(option.costing_method);
                const pricingDetails = getOptionPricingDetails(
                    option,
                    price,
                    productCount,
                    jobSubtotalForOptions,
                    isEditing,
                    userProfile?.currencyType || '$'
                );
                let label: React.ReactNode;
                if (costingMethod === CostingMethod.TO_BE_QUOTED) {
                    label = 'TBC';
                } else if (option.distance_pending) {
                    label = 'Waiting for delivery distance';
                } else if (option.price_confirmed_after_review) {
                    label = 'Price confirmed after review';
                } else if (price === null) {
                    label =
                        (costingMethod === CostingMethod.FIXED_PRODUCT &&
                            productCount == null) ||
                        (costingMethod === CostingMethod.PERCENTAGE &&
                            jobSubtotalForOptions == null)
                            ? 'Not calculated yet'
                            : getPriceLabel(
                                  option,
                                  price,
                                  userProfile?.currencyType || '$'
                              );
                } else {
                    const formatted = String(
                        formatPrice(price, {
                            currencyType: userProfile?.currencyType,
                        })
                    );
                    label =
                        Number(option.price_type) === PriceType.INDICATIVE
                            ? `${formatted} (estimate)`
                            : formatted;
                }
                return [
                    {
                        id: option.id,
                        name: option.name,
                        price,
                        label,
                        pricingDetails,
                    },
                ];
            })
        );
    }, [
        groups,
        isEditing,
        productCount,
        jobSubtotalForOptions,
        selectedIds,
        userProfile?.currencyType,
    ]);
    const livePreliminaryBreakdown = useMemo(() => {
        if (!jobCost || hasPendingOptionCalculation) return null;
        const roundMoney = (amount: number): number =>
            Math.round(amount * 100) / 100;
        const savedPreliminary = Number(jobCost.preliminaryJobCost || 0);
        const savedExtraCost = Number(jobCost.packagingOptionsCost || 0);
        const jobSubtotal = roundMoney(savedPreliminary - savedExtraCost);
        const tax = roundMoney(
            (Number(jobCost.jobSubtotal || 0) + selectedExtraTotal) *
                Number(jobCost.taxOnlyRate || 0)
        );
        const total = roundMoney(jobSubtotal + selectedExtraTotal + tax);

        return {
            jobSubtotal,
            tax,
            total,
        };
    }, [hasPendingOptionCalculation, jobCost, selectedExtraTotal]);

    const syncSelected = useCallback(
        (ids: number[]) => {
            const unique = Array.from(new Set(ids));
            selectedRef.current = unique;
            setSelectedIds(unique);
            void setFieldValue('packagingOptionIds', unique);
            void setFieldValue('hasPackagingOptions', unique.length > 0);
        },
        [setFieldValue]
    );

    useEffect(() => {
        if (!isEditing || !jobId) return;
        const currentJobId = Number(jobId);
        if (initializedJobIdRef.current === currentJobId) return;

        initializedJobIdRef.current = currentJobId;
        initializedRef.current = false;
        selectedRef.current = [];
        manuallyDeselectedRef.current = new Set();
        previousAvailableRef.current = new Set();
        previousAvailabilityContextRef.current = '';
        setSelectedIds([]);
        setSelectionAdjusted(false);
        void setFieldValue('packagingOptionIds', []);
        void setFieldValue('hasPackagingOptions', false);
        void setFieldValue('packagingOptionsLoaded', false);
        void setFieldValue('packagingOptionsValid', false);
    }, [isEditing, jobId, setFieldValue]);

    useEffect(() => {
        if (!groups.length || resolveDistance) return;
        if (
            groups.some((group) =>
                group.options.some((option) => option.distance_pending)
            )
        ) {
            setResolvedDistanceContext(distanceContextKey);
        }
    }, [distanceContextKey, groups, resolveDistance]);

    useEffect(() => {
        if (!isEditing || !jobId || reconciledJobId === Number(jobId)) return;
        let active = true;
        setReconcileFailed(false);
        void reconcileJobOptions({jobId: Number(jobId)})
            .unwrap()
            .then(() => refetchSavedOptions().unwrap())
            .then(() => {
                if (active) setReconciledJobId(Number(jobId));
            })
            .catch(() => {
                if (active) setReconcileFailed(true);
            });
        return () => {
            active = false;
        };
    }, [
        isEditing,
        jobId,
        reconcileAttempt,
        reconcileJobOptions,
        reconciledJobId,
        refetchSavedOptions,
    ]);

    useEffect(() => {
        if ((isLoadingAvailable || isFetchingAvailable) && !groups.length) {
            return;
        }
        if (availableGroupsError) return;
        if (
            isEditing &&
            (!jobId ||
                !isReconciled ||
                isLoadingSaved ||
                isFetchingSaved ||
                savedOptionsError ||
                !savedOptionsLoaded)
        )
            return;

        if (previousAvailabilityContextRef.current !== distanceContextKey) {
            previousAvailableRef.current = new Set();
            previousAvailabilityContextRef.current = distanceContextKey;
        }
        const newlyAvailable = new Set<number>();
        visibleOptionIds.forEach((id) => {
            if (!previousAvailableRef.current.has(id)) newlyAvailable.add(id);
        });
        const wasInitialized = initializedRef.current;
        const hasAvailableSavedSelection = Array.from(savedOptionIds).some(
            (id) => visibleOptionIds.has(id)
        );
        const shouldApplyPreselection = !hasAvailableSavedSelection;
        let next = selectedRef.current.filter((id) => visibleOptionIds.has(id));
        if (!wasInitialized) {
            if (isEditing && savedOptionsLoaded && !shouldApplyPreselection) {
                manuallyDeselectedRef.current = new Set();
                next = Array.from(savedOptionIds).filter((id) =>
                    visibleOptionIds.has(id)
                );
                if (
                    savedOptions.some((option) => {
                        if (option.packaging_option_id === null) return false;
                        const optionId = Number(option.packaging_option_id);
                        return (
                            Number.isSafeInteger(optionId) &&
                            !visibleOptionIds.has(optionId)
                        );
                    })
                ) {
                    setSelectionAdjusted(true);
                }
            } else if (
                !isEditing ||
                enforceRequiredGroups ||
                shouldApplyPreselection
            ) {
                next = groups.flatMap((group) =>
                    group.options
                        .filter((option) => option.preselected)
                        .map((option) => option.id)
                );
            }
            initializedRef.current = true;
        } else {
            groups.forEach((group) => {
                const singleChoiceGroup =
                    group.selection_mode !== GroupSelectionMode.ANY;
                let hasSelectedOption =
                    singleChoiceGroup &&
                    group.options.some((option) => next.includes(option.id));
                group.options.forEach((option) => {
                    if (
                        newlyAvailable.has(option.id) &&
                        (isEditing && !shouldApplyPreselection
                            ? savedOptionIds.has(option.id)
                            : option.preselected) &&
                        !manuallyDeselectedRef.current.has(option.id) &&
                        (!singleChoiceGroup || !hasSelectedOption)
                    ) {
                        next.push(option.id);
                        if (singleChoiceGroup) hasSelectedOption = true;
                    }
                });
            });
        }

        groups.forEach((group) => {
            if (
                (!isEditing || enforceRequiredGroups) &&
                group.selection_mode === GroupSelectionMode.REQUIRED &&
                group.options.length === 1 &&
                !group.options.some((option) =>
                    manuallyDeselectedRef.current.has(option.id)
                ) &&
                !group.options.some((option) => next.includes(option.id))
            ) {
                next.push(group.options[0].id);
            }
            if (group.selection_mode !== GroupSelectionMode.ANY) {
                const selectedInGroup = group.options.filter((option) =>
                    next.includes(option.id)
                );
                selectedInGroup.slice(1).forEach((option) => {
                    next = next.filter((id) => id !== option.id);
                });
            }
        });
        next = Array.from(new Set(next));
        const removedSelection =
            wasInitialized &&
            selectedRef.current.some((id) => !visibleOptionIds.has(id));
        const changed =
            next.length !== selectedRef.current.length ||
            next.some((id) => !selectedRef.current.includes(id));
        const formikIds = values.packagingOptionIds ?? [];
        const formikOutOfSync =
            formikIds.length !== next.length ||
            next.some((id) => !formikIds.includes(id));
        if (changed || !wasInitialized || formikOutOfSync) syncSelected(next);
        if (
            isEditing &&
            Array.from(savedOptionIds).every((id) => visibleOptionIds.has(id))
        ) {
            setSelectionAdjusted(false);
        }
        if (removedSelection) setSelectionAdjusted(true);
        previousAvailableRef.current = visibleOptionIds;
    }, [
        availableGroupsError,
        distanceContextKey,
        groups,
        isEditing,
        enforceRequiredGroups,
        isReconciled,
        jobId,
        isFetchingAvailable,
        isFetchingSaved,
        isLoadingAvailable,
        isLoadingSaved,
        savedOptions,
        savedOptionIds,
        savedOptionsError,
        savedOptionsLoaded,
        syncSelected,
        visibleOptionIds,
    ]);

    useEffect(() => {
        void setFieldValue(
            'packagingOptionsLoaded',
            !isFetchingAvailable &&
                !availableGroupsError &&
                !isReconciling &&
                !reconcileFailed &&
                isReconciled &&
                (!isEditing ||
                    (!isFetchingSaved &&
                        savedOptionsLoaded &&
                        !savedOptionsError))
        );
        void setFieldValue(
            'packagingOptionsValid',
            missingRequiredGroups.length === 0
        );
    }, [
        availableGroupsError,
        isEditing,
        isFetchingAvailable,
        isFetchingSaved,
        isReconciled,
        isReconciling,
        missingRequiredGroups,
        reconcileFailed,
        savedOptionsError,
        savedOptionsLoaded,
        setFieldValue,
    ]);

    const selectOption = useCallback(
        (
            group: DeliveryExtraGroup,
            option: PackagingOption,
            checked: boolean
        ) => {
            setSelectionAdjusted(false);
            const inGroup = group.options.map((item) => item.id);
            let next = selectedRef.current.filter(
                (id) => !inGroup.includes(id)
            );
            if (checked) {
                if (group.selection_mode === GroupSelectionMode.ANY) {
                    next = [...selectedRef.current, option.id];
                } else {
                    selectedRef.current
                        .filter(
                            (id) => inGroup.includes(id) && id !== option.id
                        )
                        .forEach((id) => manuallyDeselectedRef.current.add(id));
                    next = [...next, option.id];
                }
                manuallyDeselectedRef.current.delete(option.id);
            } else {
                next = selectedRef.current.filter((id) => id !== option.id);
                manuallyDeselectedRef.current.add(option.id);
                if (
                    group.selection_mode === GroupSelectionMode.REQUIRED &&
                    group.options.length === 1
                ) {
                    next.push(option.id);
                    manuallyDeselectedRef.current.delete(option.id);
                }
            }
            next = Array.from(new Set(next));
            syncSelected(next);
        },
        [syncSelected]
    );

    const selectNone = useCallback(
        (group: DeliveryExtraGroup) => {
            const ids = group.options.map((option) => option.id);
            ids.forEach((id) => manuallyDeselectedRef.current.add(id));
            const next = selectedRef.current.filter((id) => !ids.includes(id));
            syncSelected(next);
        },
        [syncSelected]
    );
    const handleRetryGroups = useCallback(
        (): void => void refetchGroups(),
        [refetchGroups]
    );
    const handleRetrySavedOptions = useCallback(
        (): void => void refetchSavedOptions(),
        [refetchSavedOptions]
    );
    const handleRetryReconcile = useCallback(
        () => setReconcileAttempt((attempt) => attempt + 1),
        []
    );
    const handleSelectNone = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const groupId = Number(event.currentTarget.dataset.groupId);
            const group = groups.find((item) => item.id === groupId);
            if (group) {
                setSelectionAdjusted(false);
                selectNone(group);
            }
        },
        [groups, selectNone]
    );
    const handleOptionChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const groupId = Number(event.currentTarget.dataset.groupId);
            const optionId = Number(event.currentTarget.dataset.optionId);
            const group = groups.find((item) => item.id === groupId);
            const option = group?.options.find((item) => item.id === optionId);
            if (group && option) {
                selectOption(group, option, event.currentTarget.checked);
            }
        },
        [groups, selectOption]
    );

    if (
        (isEditing &&
            (!jobId || !isReconciled || isLoadingSaved || isFetchingSaved)) ||
        (!groups.length &&
            (isLoadingAvailable ||
                isFetchingAvailable ||
                isLoadingSaved ||
                isFetchingSaved)) ||
        (!isReconciled && isLoadingSaved) ||
        isReconciling
    ) {
        return (
            <PackagingOptionsCollapseContainer
                initExpanded={isEditing || enforceRequiredGroups}
                iconName="Packaging.svg"
                headerText="Delivery & Extras"
                infoText="Choose the freight, packaging and services for this job."
                className="job-packaging-options">
                <Message role="status">Loading Delivery &amp; Extras…</Message>
            </PackagingOptionsCollapseContainer>
        );
    }

    if (
        !groups.length &&
        !availableGroupsError &&
        !reconcileFailed &&
        !(isEditing && savedOptionsError)
    ) {
        return null;
    }

    return (
        <PackagingOptionsCollapseContainer
            initExpanded={
                !isEditing ||
                enforceRequiredGroups ||
                availableGroupsError ||
                reconcileFailed
            }
            iconName="Packaging.svg"
            headerText="Delivery & Extras"
            infoText="Choose the freight, packaging and services for this job."
            className="job-packaging-options">
            {availableGroupsError ? (
                <Message role="alert" $warning>
                    Delivery &amp; Extras could not be loaded. Retry before
                    saving this job.
                    <br />
                    <button type="button" onClick={handleRetryGroups}>
                        Retry loading options
                    </button>
                </Message>
            ) : null}
            {isEditing && savedOptionsError ? (
                <Message role="alert" $warning>
                    Saved options could not be loaded. Retry before saving so
                    existing choices are preserved.
                    <br />
                    <button type="button" onClick={handleRetrySavedOptions}>
                        Retry saved options
                    </button>
                </Message>
            ) : null}
            {isEditing && reconcileFailed ? (
                <Message role="alert" $warning>
                    Saved Delivery &amp; Extras choices could not be refreshed.
                    Retry before saving this job.
                    <br />
                    <button type="button" onClick={handleRetryReconcile}>
                        Retry refreshing choices
                    </button>
                </Message>
            ) : null}
            {selectionAdjusted ? (
                <Message role="status" $warning>
                    Choices that are no longer available for this dispatch
                    method, depot or address were removed.
                    {!isEditing
                        ? ' Available pre-selected options were added again.'
                        : ''}
                </Message>
            ) : null}
            {groups.map((group) => {
                const selectedInGroup = group.options.find((option) =>
                    selectedIds.includes(option.id)
                );
                const mode = Number(group.selection_mode);
                const helper =
                    mode === GroupSelectionMode.REQUIRED
                        ? 'Choose one'
                        : mode === GroupSelectionMode.OPTIONAL
                          ? 'Optional'
                          : 'Choose any that apply';
                return (
                    <GroupOptions
                        key={group.id}
                        aria-label={group.customer_heading}>
                        <legend>
                            {group.customer_heading}{' '}
                            <span
                                style={{
                                    fontWeight: 400,
                                    color: 'rgba(255, 255, 255, 0.85)',
                                }}>
                                {helper}
                            </span>
                        </legend>
                        <OptionList>
                            {mode === GroupSelectionMode.OPTIONAL ? (
                                <OptionRow $selected={!selectedInGroup}>
                                    <OptionSelectLabel>
                                        <OptionRadio
                                            $selected={!selectedInGroup}>
                                            <OptionInput
                                                type="radio"
                                                name={`delivery-extra-${group.id}`}
                                                checked={!selectedInGroup}
                                                data-group-id={group.id}
                                                onChange={handleSelectNone}
                                                aria-label={`Select none under ${group.customer_heading}`}
                                            />
                                            {!selectedInGroup ? (
                                                <Icon
                                                    color="secondary"
                                                    iconName="Button-Tick-Secondary.svg"
                                                />
                                            ) : null}
                                        </OptionRadio>
                                        None
                                    </OptionSelectLabel>
                                </OptionRow>
                            ) : null}
                            {group.options.map((option) => {
                                const selected = selectedIds.includes(
                                    option.id
                                );
                                const price = getOptionPrice(
                                    option,
                                    productCount,
                                    jobSubtotalForOptions
                                );
                                const priceDetails = getOptionPricingDetails(
                                    option,
                                    price,
                                    productCount,
                                    jobSubtotalForOptions,
                                    isEditing,
                                    userProfile?.currencyType || '$'
                                );
                                const isCheckbox =
                                    mode === GroupSelectionMode.ANY;
                                const inputType = isCheckbox
                                    ? 'checkbox'
                                    : 'radio';
                                const priceTooltipId = `delivery-extra-price-${option.id}`;
                                return (
                                    <OptionRow
                                        key={option.id}
                                        $selected={selected}>
                                        <OptionSelectLabel>
                                            <OptionRadio $selected={selected}>
                                                <OptionInput
                                                    type={inputType}
                                                    name={
                                                        isCheckbox
                                                            ? `delivery-extra-${group.id}-${option.id}`
                                                            : `delivery-extra-${group.id}`
                                                    }
                                                    checked={selected}
                                                    data-group-id={group.id}
                                                    data-option-id={option.id}
                                                    onChange={
                                                        handleOptionChange
                                                    }
                                                    aria-label={`Select ${option.name}`}
                                                />
                                                {selected ? (
                                                    <Icon
                                                        color="secondary"
                                                        iconName="Button-Tick-Secondary.svg"
                                                    />
                                                ) : null}
                                            </OptionRadio>
                                            <OptionInfo>
                                                <OptionTop>
                                                    <OptionName>
                                                        {option.name}
                                                    </OptionName>
                                                    <Price>
                                                        {getOptionListPriceLabel(
                                                            option,
                                                            price,
                                                            userProfile?.currencyType ||
                                                                '$'
                                                        )}
                                                    </Price>
                                                </OptionTop>
                                                {option.description ? (
                                                    <OptionDescription>
                                                        {option.description}
                                                    </OptionDescription>
                                                ) : null}
                                                {Number(
                                                    option.costing_method
                                                ) ===
                                                    CostingMethod.BY_DISTANCE &&
                                                option.distance_km != null ? (
                                                    <OptionDescription>
                                                        {Math.round(
                                                            Number(
                                                                option.distance_km
                                                            )
                                                        )}{' '}
                                                        km
                                                    </OptionDescription>
                                                ) : null}
                                            </OptionInfo>
                                        </OptionSelectLabel>
                                        {renderPricingDetails({
                                            tooltipId: priceTooltipId,
                                            label: `Pricing details for ${option.name}`,
                                            details: priceDetails,
                                        })}
                                    </OptionRow>
                                );
                            })}
                        </OptionList>
                    </GroupOptions>
                );
            })}

            {selectedIds.length ? (
                <>
                    <ExtraBreakdown>
                        {livePreliminaryBreakdown ? (
                            <ExtraLine>
                                <span>Job Subtotal</span>
                                <span>
                                    {formatPrice(
                                        livePreliminaryBreakdown.jobSubtotal,
                                        {
                                            currencyType:
                                                userProfile?.currencyType,
                                        }
                                    )}
                                </span>
                            </ExtraLine>
                        ) : null}
                        {selectedExtraLines.map((line) => (
                            <ExtraLine key={line.id}>
                                <ExtraLineLabel>
                                    <span>{line.name}</span>
                                    {renderPricingDetails({
                                        tooltipId: `delivery-extra-breakdown-${line.id}`,
                                        label: `Pricing breakdown for ${line.name}`,
                                        details: line.pricingDetails,
                                        openRight: true,
                                    })}
                                </ExtraLineLabel>
                                <span>{line.label}</span>
                            </ExtraLine>
                        ))}
                        {livePreliminaryBreakdown ? (
                            <ExtraLine>
                                <span>{job?.countryTaxRateName || 'GST'}</span>
                                <span>
                                    {formatPrice(livePreliminaryBreakdown.tax, {
                                        currencyType: userProfile?.currencyType,
                                    })}
                                </span>
                            </ExtraLine>
                        ) : null}
                    </ExtraBreakdown>
                    {!livePreliminaryBreakdown ? (
                        <ExtraTotal aria-live="polite">
                            <span>Preliminary Cost</span>
                            <span>{preliminaryCostUnavailableMessage}</span>
                        </ExtraTotal>
                    ) : null}
                </>
            ) : null}
            {livePreliminaryBreakdown && selectedIds.length ? (
                <ExtraTotal aria-live="polite">
                    <span>
                        {hasIndicativeSelection
                            ? 'Estimated total (inc. GST)'
                            : 'Preliminary Cost'}
                    </span>
                    <span>
                        {formatPrice(livePreliminaryBreakdown.total, {
                            currencyType: userProfile?.currencyType,
                        })}
                    </span>
                </ExtraTotal>
            ) : null}
        </PackagingOptionsCollapseContainer>
    );
};

export default PackagingOptionsSection;
